import React, { useEffect, useState } from 'react';
import { QUOTA_LABELS } from '../lib/freePlan';
import { Activity, AudioLines, Camera, Wallet, CreditCard, Repeat, Plane, Gem, ArrowLeftRight } from 'lucide-react';
import './FreePlanUsage.css';

const icons = { movements_daily: ArrowLeftRight, movements_weekly: Activity, audio: AudioLines,
  photo: Camera, accounts: Wallet, cards: CreditCard, subscriptions: Repeat, trips: Plane, assets: Gem };
const resetLabel = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('es-CO', {
    day: 'numeric', month: 'short',
  }).format(date);
};

export default function FreePlanUsage({ api, compact = false, refreshKey = '', variant = 'default' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const result = await api.get('/free-plan/usage');
        if (!result.data?.access || !result.data?.quotas) throw new Error('Invalid quota response');
        if (live) { setData(result.data); setError(false); }
      }
      catch { if (live) setError(true); }
    };
    load();
    window.addEventListener('aorra:quota-updated', load);
    return () => { live = false; window.removeEventListener('aorra:quota-updated', load); };
  }, [api, refreshKey]);
  if (error) return <p role="status" className="text-sm text-slate-500">No se pudo consultar tu consumo. Actualiza la página para volver a intentarlo.</p>;
  if (!data || (compact && data.access.tier !== 'free')) return null;
  const end = data.access.free_ends_at;
  if (variant === 'plan' && !compact) return <section className="aorra-plan-usage" aria-label="Consumo del plan">
    <header className="aorra-plan-usage-header">
      <span className="aorra-plan-usage-symbol" aria-hidden="true"><Activity /></span>
      <div><h2>Consumo de tu plan</h2><p>Consulta tu actividad y los recursos disponibles.</p></div>
    </header>
    {!data.access.is_paid && <p className="aorra-plan-usage-note">{end ? `Acceso gratuito hasta ${new Date(end).toLocaleDateString('es-CO')}` : 'Acceso de por vida'}</p>}
    {data.media_owner_id !== data.owner_id && <p className="aorra-plan-usage-note">Los movimientos y recursos corresponden al propietario del tablero; los audios y fotos, a tu usuario.</p>}
    <div className="aorra-plan-usage-grid">
      {Object.entries(data.quotas).map(([key, quota]) => {
        const Icon = icons[key] || Activity;
        const used = quota.used ?? 0;
        const finite = Number.isFinite(quota.limit) && quota.limit > 0;
        const progress = finite ? Math.min(100, Math.max(0, used / quota.limit * 100)) : 0;
        return <div className="aorra-plan-usage-item" key={key}>
          <div className="aorra-plan-usage-label"><Icon aria-hidden="true" /><span>{QUOTA_LABELS[key] || key}</span></div>
          <p className="aorra-plan-usage-value"><strong>{used}</strong><span> / {quota.limit ?? 'Sin límite'}</span></p>
          {finite && <div className="aorra-plan-usage-track" role="progressbar" aria-label={QUOTA_LABELS[key] || key} aria-valuemin={0} aria-valuemax={quota.limit} aria-valuenow={Math.min(quota.limit, Math.max(0, used))}><span style={{ width: `${progress}%` }} /></div>}
          {quota.resets_at && <p className="aorra-plan-usage-reset">Se renueva {resetLabel(quota.resets_at)}</p>}
          {quota.reached && <p className="aorra-plan-usage-reached">Cupo alcanzado. Puedes consultar tus datos y mejorar tu plan.</p>}
        </div>;
      })}
    </div>
  </section>;
  return <section className="mb-5 rounded-xl border border-slate-200 dark:border-slate-700 p-4" aria-label="Consumo del plan">
    <h3 className="font-semibold">{data.access.is_paid ? 'Consumo de tu plan' : 'Plan Gratis'}</h3>
    {!data.access.is_paid && <p className="text-sm text-slate-500">{end ? `Acceso gratuito hasta ${new Date(end).toLocaleString('es-CO')}` : 'Acceso de por vida'}</p>}
    {!compact && data.media_owner_id !== data.owner_id && <p className="text-xs text-slate-500">Los movimientos y recursos corresponden al propietario del tablero; los audios y fotos, a tu usuario.</p>}
    <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-3">
      {Object.entries(data.quotas).filter(([key]) => !compact || key.startsWith('movements_')).map(([key, quota]) => <div key={key} className="text-sm">
        <p>{QUOTA_LABELS[key]}: <strong>{quota.used} / {quota.limit ?? 'Sin límite'}</strong></p>
        {!compact && quota.resets_at && <p className="text-xs text-slate-500">Se restablece: {new Date(quota.resets_at).toLocaleString('es-CO')}</p>}
        {quota.reached && <p className="text-xs text-amber-600">Cupo alcanzado. Puedes consultar tus datos y mejorar tu plan.</p>}
      </div>)}
    </div>
  </section>;
}
